import { useCallbackProxy } from '@timelyapp/react';
import { useCallback, useRef } from 'react';

/**
 * Makes sure a callback is only fired again once the previous invocation has completed.
 */
export const useSequentialCallback = <
  TArgs extends Array<any>,
  T extends (...args: TArgs) => Promise<any>,
>(
  callback: T
): ((...args: TArgs) => void) => {
  // Let callback change without causing us to return a new function instance
  const proxiedCallback = useCallbackProxy(callback);
  const queue = useRef<Array<TArgs>>([]);
  const currentPromise = useRef<Promise<unknown>>();
  const callNext = useCallback(
    (args: TArgs) => {
      if (currentPromise.current) {
        queue.current.push(args);
      } else {
        currentPromise.current = proxiedCallback(...args).finally(() => {
          currentPromise.current = undefined;
          if (queue.current.length > 0) {
            callNext(queue.current.shift()!);
          }
        });
      }
    },
    [proxiedCallback]
  );
  return useCallback(
    (...args: TArgs) => {
      callNext(args);
    },
    [callNext]
  );
};
